import { logActivity } from '../utils/activityLog';
import { fmtDate } from '../utils/format';
import React, { useState, useEffect } from 'react';
import { Plus, Filter, Pencil, Trash2, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { toast } from 'sonner';
import Users from './Users';
import { getCurrentUser, hasPermission } from '../utils/roles';

const STATUS_BADGE = active =>
  active
    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
    : 'bg-red-500/20 text-red-400 border border-red-500/30';

// ── Root component ────────────────────────────────────────────────────────────
export default function Offices() {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const isDark = theme === 'dark';
  const currentUser = getCurrentUser();
  const currentRole = currentUser.role;
  const isOwner = currentRole === 'owner';
  const canSeeOffices = hasPermission(currentUser, 'View Offices');
  const canSeeUsers = hasPermission(currentUser, 'View Users');
  const canCreateOffice = hasPermission(currentUser, 'Create Office');
  const canEditOffice = hasPermission(currentUser, 'Edit Office');
  const canDeleteOffice = hasPermission(currentUser, 'Delete Office');

  const TABS = [
    ...(canSeeOffices ? ['offices', 'desks', 'teams'] : []),
    ...(canSeeUsers ? ['users'] : []),
  ];
  const [tab, setTab] = useState(TABS[0]);

  const cls = {
    card:       `rounded-lg border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`,
    input:      `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white placeholder-gray-600' : 'bg-white border-gray-300 text-gray-900'}`,
    inputDis:   `w-full px-3 py-2 border rounded-md text-sm ${isDark ? 'bg-[#060a14] border-gray-800 text-gray-600' : 'bg-gray-100 border-gray-200 text-gray-400'} cursor-not-allowed`,
    select:     `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`,
    selectDis:  `w-full px-3 py-2 border rounded-md text-sm opacity-50 cursor-not-allowed ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-gray-500' : 'bg-gray-100 border-gray-300 text-gray-400'}`,
    label:      `block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`,
    th:         `px-3 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`,
    td:         `px-3 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`,
    btn:        `px-3 py-1.5 text-sm border rounded-md transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`,
    btnPrimary: 'px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors',
  };

  return (
    <div className="p-6 space-y-4">
      {/* Tab bar */}
      <div className={`flex border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        {TABS.map(tabKey => (
          <button
            key={tabKey}
            onClick={() => setTab(tabKey)}
            className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === tabKey
                ? 'border-purple-500 text-purple-500'
                : `border-transparent ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'}`
            }`}
          >
            {t(`offices.tabs.${tabKey}`)}
          </button>
        ))}
      </div>

      {tab === 'offices' && canSeeOffices && <OfficesTab cls={cls} isDark={isDark} isOwner={isOwner} canCreate={canCreateOffice} canEdit={canEditOffice} canDelete={canDeleteOffice} />}
      {tab === 'desks'   && <DesksTab   cls={cls} isDark={isDark} isOwner={isOwner} canCreate={canCreateOffice} canEdit={canEditOffice} canDelete={canDeleteOffice} />}
      {tab === 'teams'   && <TeamsTab   cls={cls} isDark={isDark} isOwner={isOwner} canCreate={canCreateOffice} canEdit={canEditOffice} canDelete={canDeleteOffice} />}
      {tab === 'users'   && <div className="-mx-6 -mt-4"><Users /></div>}
    </div>
  );
}

// ─── Offices Tab ──────────────────────────────────────────────────────────────
function OfficesTab({ cls, isDark, isOwner, canCreate, canEdit, canDelete }) {
  const { t } = useLanguage();
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ search: '' });
  const [showModal, setShowModal] = useState(false);
  const [editOffice, setEditOffice] = useState(null);
  const [form, setForm] = useState({ name: '', cabinet: '', cabinet_url: '', brand_name: '', hasBrandLogo: false });
  const [logoBusy, setLogoBusy] = useState(false);
  const logoInputRef = React.useRef(null);

  useEffect(() => { logActivity('view', 'offices', null, 'Viewed Offices list'); }, []);
  useEffect(() => { loadOffices(); }, [activeFilter]); // eslint-disable-line

  const loadOffices = async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeFilter !== null) params.active = activeFilter;
      if (filters.search) params.search = filters.search;
      const { data } = await api.get('/offices', { params });
      setOffices(data);
    } catch { toast.error(t('offices.failedLoadOffices')); }
    finally { setLoading(false); }
  };

  const openNew  = () => { if (!canCreate) return; setEditOffice(null); setForm({ name: '', cabinet: '', cabinet_url: '', brand_name: '', hasBrandLogo: false }); setShowModal(true); };
  const openEdit = (o) => { if (!canEdit) return; setEditOffice(o); setForm({ name: o.name, cabinet: o.cabinet || '', cabinet_url: o.cabinetUrl || '', brand_name: o.brandName || '', hasBrandLogo: !!o.hasBrandLogo }); setShowModal(true); };

  const uploadBrandLogo = async (file) => {
    if (!file || !editOffice) return;
    const fd = new FormData();
    fd.append('file', file);
    setLogoBusy(true);
    try {
      await api.post(`/offices/${editOffice.id}/brand-logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(p => ({ ...p, hasBrandLogo: true }));
      toast.success(t('offices.logoUploaded'));
      loadOffices();
    } catch (e) { toast.error(e.response?.data?.detail || t('offices.saveFailed')); }
    finally { setLogoBusy(false); }
  };

  const removeBrandLogo = async () => {
    if (!editOffice) return;
    setLogoBusy(true);
    try {
      await api.delete(`/offices/${editOffice.id}/brand-logo`);
      setForm(p => ({ ...p, hasBrandLogo: false }));
      toast.success(t('offices.logoRemoved'));
      loadOffices();
    } catch (e) { toast.error(e.response?.data?.detail || t('offices.saveFailed')); }
    finally { setLogoBusy(false); }
  };

  const saveOffice = async () => {
    if (editOffice && !canEdit) return;
    if (!editOffice && !canCreate) return;
    if (!form.name) { toast.error(t('offices.officeNameRequired')); return; }
    try {
      const payload = { name: form.name, cabinet: form.cabinet || null, cabinet_url: form.cabinet_url || null, brand_name: form.brand_name || null };
      if (editOffice) { await api.put(`/offices/${editOffice.id}`, payload); toast.success(t('offices.officeUpdated')); }
      else { await api.post('/offices', payload); toast.success(t('offices.officeCreated')); }
      setShowModal(false); loadOffices();
    } catch (e) { toast.error(e.response?.data?.detail || t('offices.saveFailed')); }
  };

  const deleteOffice = async (o) => {
    if (!canDelete) return;
    if (!window.confirm(t('offices.confirmDeleteOffice').replace('{name}', o.name))) return;
    try { await api.delete(`/offices/${o.id}`); toast.success(t('offices.officeDeleted')); loadOffices(); }
    catch (e) { toast.error(e.response?.data?.detail || t('offices.deleteFailed')); }
  };

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {canCreate && (
            <button onClick={openNew} className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700">
              <Plus size={14} /> {t('offices.new')}
            </button>
          )}
          <button onClick={() => setActiveFilter(null)}  className={`px-4 py-1.5 text-sm rounded-md border transition-colors ${activeFilter === null  ? 'bg-purple-600 text-white border-purple-600' : isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>{t('offices.active')}</button>
          <button onClick={() => setActiveFilter(false)} className={`px-4 py-1.5 text-sm rounded-md border transition-colors ${activeFilter === false ? 'bg-red-600 text-white border-red-600'     : isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>{t('offices.inactive')}</button>
          <button onClick={() => setShowFilters(v => !v)} className={`flex items-center gap-1.5 ${cls.btn}`}>
            <Filter size={14} /> {t('offices.filters')} {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
        <button onClick={loadOffices} className={cls.btn}><RefreshCw size={14} /></button>
      </div>

      {showFilters && (
        <div className={`${cls.card} p-4`}>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={cls.label}>ID</label><input className={cls.input} placeholder={t('offices.enterValue')} /></div>
            <div>
              <label className={cls.label}>{t('offices.name')}</label>
              <input value={filters.search} onChange={e => setFilters(p => ({ ...p, search: e.target.value }))} className={cls.input} placeholder={t('offices.enterValue')} />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={loadOffices} className={cls.btnPrimary}>{t('offices.apply')}</button>
            <button onClick={() => { setFilters({ search: '' }); setTimeout(loadOffices, 0); }} className={cls.btn}>{t('offices.clear')}</button>
          </div>
        </div>
      )}

      <div className={`${cls.card} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={isDark ? 'bg-[#0a0e1a] border-b border-gray-800' : 'bg-gray-50 border-b border-gray-200'}>
              <tr>{['id','name','usersCount','clientsCount','cabinetUrl','creationDate','actions'].map(h => <th key={h} className={`${cls.th} ${['usersCount','clientsCount'].includes(h) ? 'text-left' : ''}`}>{t(`offices.columns.${h}`)}</th>)}</tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {loading ? (
                <tr><td colSpan={7} className={`${cls.td} text-center py-12`}>{t('offices.loading')}</td></tr>
              ) : offices.length === 0 ? (
                <tr><td colSpan={7} className={`${cls.td} text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('offices.noOffices')}</td></tr>
              ) : offices.map(o => (
                <tr key={o.id} className={isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50'}>
                  <td className={cls.td}><span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>#{o.id}</span></td>
                  <td className={`${cls.td} font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{o.name}</td>
                  <td className={`${cls.td} text-left tabular-nums`}>{o.usersCount}</td>
                  <td className={`${cls.td} text-left tabular-nums`}>{o.clientsCount}</td>
                  <td className={cls.td}>
                    {o.cabinetUrl
                      ? (
                        <div>
                          {o.cabinet && <div className={`text-xs mb-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{o.cabinet}</div>}
                          <a
                            href={o.cabinetUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-400 hover:text-blue-300 underline truncate max-w-[200px] block"
                          >
                            {o.cabinetUrl}
                          </a>
                        </div>
                      )
                      : '—'}
                  </td>
                  <td className={cls.td}>{fmtDate(o.createdAt)}</td>
                  <td className={cls.td}>
                    <div className="flex items-center gap-2">
                      {canEdit && <button onClick={() => openEdit(o)} className={`p-1.5 rounded transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}><Pencil size={14} /></button>}
                      {canDelete && <button onClick={() => deleteOffice(o)} className="p-1.5 rounded text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 size={14} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={`px-4 py-2 text-xs border-t ${isDark ? 'border-gray-800 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
          {offices.length} {t('offices.entries')}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-lg shadow-2xl ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{editOffice ? t('offices.editOffice') : t('offices.addOffice')}</h2>
              <button onClick={() => setShowModal(false)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}>✕</button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div><label className={cls.label}>{t('offices.cabinet')}</label><input value={form.cabinet} onChange={e => setForm(p => ({ ...p, cabinet: e.target.value }))} className={cls.input} placeholder={t('offices.selectCabinet')} /></div>
              <div><label className={cls.label}>{t('offices.officeName')} *</label><input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className={cls.input} placeholder={t('offices.typeOfficeName')} /></div>
              <div><label className={cls.label}>Cabinet URL</label><input value={form.cabinet_url} onChange={e => setForm(p => ({ ...p, cabinet_url: e.target.value }))} className={cls.input} placeholder="https://..." /></div>
              <div><label className={cls.label}>{t('offices.brandName')}</label><input value={form.brand_name} onChange={e => setForm(p => ({ ...p, brand_name: e.target.value }))} className={cls.input} placeholder={t('offices.brandNamePlaceholder')} /></div>
              <div>
                <label className={cls.label}>{t('offices.brandLogo')}</label>
                {editOffice ? (
                  <div className="flex items-center gap-2">
                    <input ref={logoInputRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
                      onChange={e => { const f = e.target.files?.[0]; if (f) uploadBrandLogo(f); e.target.value = ''; }} />
                    <button type="button" onClick={() => logoInputRef.current?.click()} disabled={logoBusy} className={cls.btn}>
                      {logoBusy ? '...' : (form.hasBrandLogo ? t('offices.replaceLogo') : t('offices.uploadLogo'))}
                    </button>
                    {form.hasBrandLogo && (
                      <>
                        <img src={`${api.defaults.baseURL}/portal/branding/logo?host=${encodeURIComponent((() => { try { return new URL(/^https?:\/\//i.test(form.cabinet_url) ? form.cabinet_url : `https://${form.cabinet_url}`).hostname; } catch { return ''; } })())}&_t=${Date.now()}`}
                          alt="logo" className="h-8 max-w-[96px] rounded object-contain" onError={e => { e.target.style.display = 'none'; }} />
                        <button type="button" onClick={removeBrandLogo} disabled={logoBusy} className={`text-xs ${isDark ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}`}>{t('offices.removeLogo')}</button>
                      </>
                    )}
                  </div>
                ) : (
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('offices.logoAfterCreate')}</p>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowModal(false)} className={cls.btn}>{t('offices.cancel')}</button>
                <button onClick={saveOffice} className={cls.btnPrimary}>{t('offices.save')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Desks Tab ────────────────────────────────────────────────────────────────
function DesksTab({ cls, isDark, isOwner, canCreate, canEdit, canDelete }) {
  const { t } = useLanguage();
  const cu = getCurrentUser();
  const isPrivileged = ['owner', 'admin', 'crm_manager'].includes(cu.role);
  const myOffice = cu.office || '';
  const [offices, setOffices] = useState([]);
  const [desks, setDesks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterOffice, setFilterOffice] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editDesk, setEditDesk] = useState(null);
  const [form, setForm] = useState({ name: '', office_id: '', office_name: '' });

  useEffect(() => {
    api.get('/offices').then(r => setOffices(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { loadDesks(); }, [filterOffice]); // eslint-disable-line

  const loadDesks = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterOffice) params.office = filterOffice;
      const { data } = await api.get('/desks', { params });
      setDesks(data || []);
    } catch { toast.error(t('offices.failedLoadDesks')); }
    finally { setLoading(false); }
  };

  const openNew  = () => {
    if (!canCreate) return;
    // Non-privileged users auto-get their office; privileged start empty
    const fallback = !isOwner && myOffice ? offices.find(o => o.name === myOffice) : null;
    setEditDesk(null);
    setForm({ name: '', office_id: fallback ? String(fallback.id) : '', office_name: fallback ? fallback.name : '' });
    setShowModal(true);
  };
  const openEdit = (d) => {
    if (!canEdit) return;
    setEditDesk(d);
    // If desk has no office, pre-fill with user's own office as default
    const fallback = !d.officeName && myOffice ? offices.find(o => o.name === myOffice) : null;
    setForm({
      name: d.name,
      office_id: d.officeId ? String(d.officeId) : (fallback ? String(fallback.id) : ''),
      office_name: d.officeName || (fallback ? fallback.name : ''),
    });
    setShowModal(true);
  };

  const handleOfficeChange = (officeId) => {
    const o = offices.find(x => String(x.id) === officeId);
    setForm(p => ({ ...p, office_id: officeId, office_name: o ? o.name : '' }));
  };

  const saveDesk = async () => {
    if (editDesk && !canEdit) return;
    if (!editDesk && !canCreate) return;
    if (!form.name) { toast.error(t('offices.deskNameRequired')); return; }
    try {
      const payload = { name: form.name, office_id: form.office_id ? Number(form.office_id) : null, office_name: form.office_name || null };
      if (editDesk) {
        await api.put(`/desks/${editDesk.id}`, payload);
        // Cascade office change to all teams of this desk
        if (form.office_name !== (editDesk.officeName || '')) {
          const { data: deskTeams } = await api.get('/teams', { params: { desk_id: editDesk.id } });
          await Promise.all(deskTeams.map(t =>
            api.put(`/teams/${t.id}`, {
              name: t.name, department: t.department || null,
              desk_id: t.deskId || null, desk_name: t.deskName || null,
              office_id: payload.office_id, office_name: payload.office_name,
            })
          ));
          if (deskTeams.length > 0) toast.info(t('offices.updatedOfficeTeams').replace('{count}', deskTeams.length));
        }
        toast.success(t('offices.deskUpdated'));
      } else {
        await api.post('/desks', payload);
        toast.success(t('offices.deskCreated'));
      }
      setShowModal(false); loadDesks();
    } catch (e) { toast.error(e.response?.data?.detail || t('offices.saveFailed')); }
  };

  const deleteDesk = async (d) => {
    if (!canDelete) return;
    if (!window.confirm(t('offices.confirmDeleteDesk').replace('{name}', d.name))) return;
    try { await api.delete(`/desks/${d.id}`); toast.success(t('offices.deskDeleted')); loadDesks(); }
    catch (e) { toast.error(e.response?.data?.detail || t('offices.deleteFailed')); }
  };

  // Table columns: owner sees Office column too
  const headers = isOwner ? ['id','name','office','created','actions'] : ['id','name','created','actions'];

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {canCreate && (
            <button onClick={openNew} className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700">
              <Plus size={14} /> {t('offices.newDesk')}
            </button>
          )}
          <button onClick={() => setShowFilters(v => !v)} className={`flex items-center gap-1.5 ${cls.btn}`}>
            <Filter size={14} /> {t('offices.filters')} {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
        <button onClick={loadDesks} className={cls.btn}><RefreshCw size={14} /></button>
      </div>

      {showFilters && (
        <div className={`${cls.card} p-4`}>
          <div className="grid grid-cols-3 gap-3">
            {isPrivileged && (
              <div>
                <label className={cls.label}>{t('offices.office')}</label>
                <select value={filterOffice} onChange={e => setFilterOffice(e.target.value)} className={cls.select}>
                  <option value="">{t('offices.allOffices')}</option>
                  {(isOwner ? offices : offices.filter(o => o.name === myOffice))
                    .map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={loadDesks} className={cls.btnPrimary}>{t('offices.apply')}</button>
            <button onClick={() => { setFilterOffice(''); }} className={cls.btn}>{t('offices.clear')}</button>
          </div>
        </div>
      )}

      <div className={`${cls.card} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={isDark ? 'bg-[#0a0e1a] border-b border-gray-800' : 'bg-gray-50 border-b border-gray-200'}>
              <tr>{headers.map(h => <th key={h} className={cls.th}>{t(`offices.columns.${h}`)}</th>)}</tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {loading ? (
                <tr><td colSpan={headers.length} className={`${cls.td} text-center py-12`}>{t('offices.loading')}</td></tr>
              ) : desks.length === 0 ? (
                <tr><td colSpan={headers.length} className={`${cls.td} text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('offices.noDesks')}</td></tr>
              ) : desks.map(d => (
                <tr key={d.id} className={isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50'}>
                  <td className={cls.td}><span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>#{d.id}</span></td>
                  <td className={`${cls.td} font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{d.name}</td>
                  {isOwner && <td className={cls.td}>{d.officeName || '—'}</td>}
                  <td className={cls.td}>{fmtDate(d.createdAt)}</td>
                  <td className={cls.td}>
                    <div className="flex items-center gap-2">
                      {canEdit && <button onClick={() => openEdit(d)} className={`p-1.5 rounded transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}><Pencil size={14} /></button>}
                      {canDelete && <button onClick={() => deleteDesk(d)} className="p-1.5 rounded text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 size={14} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={`px-4 py-2 text-xs border-t ${isDark ? 'border-gray-800 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
          {desks.length} {t('offices.entries')}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-lg shadow-2xl ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{editDesk ? t('offices.editDesk') : t('offices.addDesk')}</h2>
              <button onClick={() => setShowModal(false)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}>✕</button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div>
                <label className={cls.label}>{t('offices.office')}</label>
                {isPrivileged ? (
                  <select value={form.office_id} onChange={e => handleOfficeChange(e.target.value)} className={cls.select}>
                    <option value="">— Select Office —</option>
                    {(isOwner ? offices : offices.filter(o => o.name === myOffice))
                      .map(o => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
                  </select>
                ) : (
                  <div className={`px-3 py-2 rounded-md text-sm border ${isDark ? 'bg-[#060a14] border-gray-800 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                    {form.office_name || '—'}
                  </div>
                )}
              </div>
              <div>
                <label className={cls.label}>{t('offices.deskName')} *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className={cls.input} placeholder={t('offices.typeDeskName')} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowModal(false)} className={cls.btn}>{t('offices.cancel')}</button>
                <button onClick={saveDesk} className={cls.btnPrimary}>{t('offices.save')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Teams Tab ────────────────────────────────────────────────────────────────
function TeamsTab({ cls, isDark, isOwner, canCreate, canEdit, canDelete }) {
  const { t } = useLanguage();
  const cu = getCurrentUser();
  const myOffice = cu.office || '';
  const [offices, setOffices] = useState([]);
  const [deskOpts, setDeskOpts] = useState([]);
  const [allDesks, setAllDesks] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterOffice, setFilterOffice] = useState('');
  const [filterDesk, setFilterDesk] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [form, setForm] = useState({ name: '', office_id: '', office_name: '', desk_id: '', desk_name: '', department: '' });

  useEffect(() => {
    api.get('/offices').then(r => setOffices(r.data || [])).catch(() => {});
    api.get('/desks').then(r => setAllDesks(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!filterOffice) { setDeskOpts([]); setFilterDesk(''); return; }
    const o = offices.find(x => x.name === filterOffice);
    if (o) api.get('/desks', { params: { office_id: o.id } }).then(r => setDeskOpts(r.data || [])).catch(() => setDeskOpts([]));
    setFilterDesk('');
  }, [filterOffice, offices]);

  useEffect(() => { loadTeams(); }, [filterOffice, filterDesk]); // eslint-disable-line

  const loadTeams = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterOffice) { const o = offices.find(x => x.name === filterOffice); if (o) params.office_id = o.id; }
      if (filterDesk)   { const d = deskOpts.find(x => x.name === filterDesk);  if (d) params.desk_id   = d.id; }
      const { data } = await api.get('/teams', { params });
      setTeams(data || []);
    } catch { toast.error(t('offices.failedLoadTeams')); }
    finally { setLoading(false); }
  };

  const openNew  = () => { if (!canCreate) return; setEditTeam(null); setForm({ name: '', office_id: '', office_name: '', desk_id: '', desk_name: '', department: '' }); setShowModal(true); };
  const openEdit = (t) => {
    if (!canEdit) return;
    setEditTeam(t);
    setForm({ name: t.name, office_id: t.officeId ? String(t.officeId) : '', office_name: t.officeName || '', desk_id: t.deskId ? String(t.deskId) : '', desk_name: t.deskName || '', department: t.department || '' });
    setShowModal(true);
  };

  const handleFormDeskChange = (deskId) => {
    const d = allDesks.find(x => String(x.id) === deskId);
    setForm(p => ({ ...p, desk_id: deskId, desk_name: d ? d.name : '', office_id: d ? String(d.officeId || '') : '', office_name: d ? (d.officeName || '') : '' }));
  };

  const saveTeam = async () => {
    if (editTeam && !canEdit) return;
    if (!editTeam && !canCreate) return;
    if (!form.name) { toast.error(t('offices.teamNameRequired')); return; }
    try {
      const payload = { name: form.name, office_id: form.office_id ? Number(form.office_id) : null, office_name: form.office_name || null, desk_id: form.desk_id ? Number(form.desk_id) : null, desk_name: form.desk_name || null, department: form.department || null };
      if (editTeam) { await api.put(`/teams/${editTeam.id}`, payload); toast.success(t('offices.teamUpdated')); }
      else { await api.post('/teams', payload); toast.success(t('offices.teamCreated')); }
      setShowModal(false); loadTeams();
    } catch (e) { toast.error(e.response?.data?.detail || t('offices.saveFailed')); }
  };

  const deleteTeam = async (t) => {
    if (!canDelete) return;
    if (!window.confirm(t('offices.confirmDeleteTeam').replace('{name}', t.name))) return;
    try { await api.delete(`/teams/${t.id}`); toast.success(t('offices.teamDeleted')); loadTeams(); }
    catch (e) { toast.error(e.response?.data?.detail || t('offices.deleteFailed')); }
  };

  const headers = isOwner ? ['id','name','department','desk','office','created','actions'] : ['id','name','department','desk','created','actions'];

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {canCreate && (
            <button onClick={openNew} className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700">
              <Plus size={14} /> {t('offices.newTeam')}
            </button>
          )}
          <button onClick={() => setShowFilters(v => !v)} className={`flex items-center gap-1.5 ${cls.btn}`}>
            <Filter size={14} /> {t('offices.filters')} {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
        <button onClick={loadTeams} className={cls.btn}><RefreshCw size={14} /></button>
      </div>

      {showFilters && (
        <div className={`${cls.card} p-4`}>
          <div className="grid grid-cols-3 gap-3">
            {isOwner && (
              <div>
                <label className={cls.label}>{t('offices.office')}</label>
                <select value={filterOffice} onChange={e => setFilterOffice(e.target.value)} className={cls.select}>
                  <option value="">{t('offices.allOffices')}</option>
                  {offices.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className={cls.label}>{t('offices.desk')}</label>
              <select
                value={filterDesk}
                onChange={e => setFilterDesk(e.target.value)}
                disabled={isOwner && (!filterOffice || deskOpts.length === 0)}
                className={(isOwner && (!filterOffice || deskOpts.length === 0)) ? cls.selectDis : cls.select}
              >
                <option value="">{(isOwner && !filterOffice) ? '— Select Office first —' : 'All Desks'}</option>
                {(isOwner ? deskOpts : allDesks).map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={loadTeams} className={cls.btnPrimary}>{t('offices.apply')}</button>
            <button onClick={() => { setFilterOffice(''); setFilterDesk(''); }} className={cls.btn}>{t('offices.clear')}</button>
          </div>
        </div>
      )}

      <div className={`${cls.card} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={isDark ? 'bg-[#0a0e1a] border-b border-gray-800' : 'bg-gray-50 border-b border-gray-200'}>
              <tr>{headers.map(h => <th key={h} className={cls.th}>{t(`offices.columns.${h}`)}</th>)}</tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {loading ? (
                <tr><td colSpan={headers.length} className={`${cls.td} text-center py-12`}>{t('offices.loading')}</td></tr>
              ) : teams.length === 0 ? (
                <tr><td colSpan={headers.length} className={`${cls.td} text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('offices.noTeams')}</td></tr>
              ) : teams.map(t => (
                <tr key={t.id} className={isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50'}>
                  <td className={cls.td}><span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>#{t.id}</span></td>
                  <td className={`${cls.td} font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.name}</td>
                  <td className={cls.td}>
                    {t.department === 'sales' && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">Sales</span>}
                    {t.department === 'retention' && <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">Retention</span>}
                    {!t.department && <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>—</span>}
                  </td>
                  <td className={cls.td}>{t.deskName || '—'}</td>
                  {isOwner && <td className={cls.td}>{t.officeName || '—'}</td>}
                  <td className={cls.td}>{fmtDate(t.createdAt)}</td>
                  <td className={cls.td}>
                    <div className="flex items-center gap-2">
                      {canEdit && <button onClick={() => openEdit(t)} className={`p-1.5 rounded transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}><Pencil size={14} /></button>}
                      {canDelete && <button onClick={() => deleteTeam(t)} className="p-1.5 rounded text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 size={14} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={`px-4 py-2 text-xs border-t ${isDark ? 'border-gray-800 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
          {teams.length} {t('offices.entries')}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-lg shadow-2xl ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{editTeam ? t('offices.editTeam') : t('offices.addTeam')}</h2>
              <button onClick={() => setShowModal(false)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}>✕</button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div>
                <label className={cls.label}>{t('offices.desk')} *</label>
                <select value={form.desk_id} onChange={e => handleFormDeskChange(e.target.value)} className={cls.select}>
                  <option value="">— Select Desk —</option>
                  {allDesks.map(d => (
                    <option key={d.id} value={String(d.id)}>{d.name}</option>
                  ))}
                </select>
              </div>
              {form.office_name && (
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t('offices.office')}: <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{form.office_name}</span>
                </div>
              )}
              <div>
                <label className={cls.label}>{t('offices.teamName')} *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className={cls.input} placeholder={t('offices.typeTeamName')} />
              </div>
              <div>
                <label className={cls.label}>{t('offices.department')}</label>
                <select value={form.department} onChange={e => setForm(p => ({ ...p, department: e.target.value }))} className={cls.select}>
                  <option value="">— Not specified —</option>
                  <option value="sales">Sales</option>
                  <option value="retention">Retention</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowModal(false)} className={cls.btn}>{t('offices.cancel')}</button>
                <button onClick={saveTeam} className={cls.btnPrimary}>{t('offices.save')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

